class BoxQuantityPickerComponent extends HTMLElement {
  /** @type {number|null} */
  #activePreset = null;
  /** @type {number|null} */
  #previousQuantity = null;
  /** @type {HTMLInputElement[]} */
  #inputs = [];
  /** @type {AbortController} */
  #abortController = null;

  connectedCallback() {
    this.#inputs = Array.from(this.querySelectorAll("input[data-quantity]"));
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#inputs.forEach((input) => {
      // 'click' fires even on an already-checked radio — used for toggle-off
      input.addEventListener("click", () => this.#handleClick(input), {
        signal,
      });
      // 'change' fires only when an unchecked radio becomes checked
      input.addEventListener("change", () => this.#handleChange(input), {
        signal,
      });
    });

    // Reset when the variant changes (morph preserves the element across re-renders)
    const productForm = this.closest("product-form-component");
    if (productForm) {
      productForm.addEventListener("variant:update", () => this.#reset(), {
        signal,
      });
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {HTMLInputElement|null} */
  get #quantityInput() {
    const form = this.closest("form");
    if (!form) return null;
    const qtySelector = form.querySelector("quantity-selector-component");
    if (qtySelector?.refs?.quantityInput instanceof HTMLInputElement) {
      return qtySelector.refs.quantityInput;
    }
    return form.querySelector('input[name="quantity"]');
  }

  /** Handles click on an already-checked radio (toggle-off). */
  #handleClick(input) {
    const preset = parseInt(input.dataset.quantity, 10);
    if (this.#activePreset !== preset) return; // not yet active; 'change' will handle activation

    // Toggle off — radio was already checked, user clicked it again
    input.checked = false;
    this.#activePreset = null;
    this.#updateDataAttributes(null, null);

    const qtyInput = this.#quantityInput;
    if (qtyInput) {
      qtyInput.value = String(this.#previousQuantity ?? 1);
      this.#previousQuantity = null;
      this.#dispatchQuantityUpdate(qtyInput);
    }
  }

  /** Handles the radio becoming checked (new preset selected). */
  #handleChange(input) {
    const preset = parseInt(input.dataset.quantity, 10);
    const qtyInput = this.#quantityInput;

    if (qtyInput) {
      // Save the pre-selection quantity only on the very first preset pick
      if (this.#activePreset === null) {
        this.#previousQuantity = parseInt(qtyInput.value, 10) || 1;
      }
      qtyInput.value = String(preset);
      this.#dispatchQuantityUpdate(qtyInput);
    }

    const prevInput =
      this.#activePreset !== null
        ? (this.#inputs.find(
            (i) => parseInt(i.dataset.quantity, 10) === this.#activePreset,
          ) ?? null)
        : null;

    this.#activePreset = preset;
    this.#updateDataAttributes(input, prevInput);
  }

  /**
   * Mirrors variant-picker's data-current-checked / data-previous-checked attributes
   * so the theme's pill slide animation works correctly across all 3 options.
   * @param {HTMLInputElement|null} current
   * @param {HTMLInputElement|null} previous
   */
  #updateDataAttributes(current, previous) {
    this.#inputs.forEach((input) => {
      input.dataset.currentChecked = input === current ? "true" : "false";
      input.dataset.previousChecked = input === previous ? "true" : "false";
    });
  }

  /** @param {HTMLInputElement} input */
  #dispatchQuantityUpdate(input) {
    // 'input' notifies price-per-item and other live listeners
    input.dispatchEvent(new Event("input", { bubbles: true }));
    // 'blur' is captured at document level by the theme's event delegation,
    // which routes it to QuantitySelectorComponent.setQuantity() → validates,
    // clamps, then fires QuantitySelectorUpdateEvent for the product form.
    input.dispatchEvent(new Event("blur"));
  }

  #reset() {
    this.#activePreset = null;
    this.#previousQuantity = null;
    this.#inputs.forEach((input) => {
      input.checked = false;
      input.dataset.currentChecked = "false";
      input.dataset.previousChecked = "false";
    });
  }
}

if (!customElements.get("box-quantity-picker")) {
  customElements.define("box-quantity-picker", BoxQuantityPickerComponent);
}
